import {
  BUSINESS_KINDS,
  IMPORT_FILE_EXTENSIONS,
  IMPORT_STATUS_LABELS,
  type ImportJobDTO,
  type ImportUploadSettings,
  MAX_IMPORT_BYTES,
  MAX_IMPORT_ROWS,
} from '@hearth/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileUp, FolderSearch, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, CardBody, CardHeader, Checkbox, EmptyState, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useLookups } from '../../hooks/useMeta';
import { api, errorMessage } from '../../lib/api';
import { ImportOptionCards, type ImportOptions, validCountry } from './ImportOptions';

export const IMPORT_STATUS_COLORS: Record<ImportJobDTO['status'], string> = {
  uploaded: 'sky',
  validating: 'sky',
  validated: 'amber',
  importing: 'sky',
  completed: 'green',
  failed: 'red',
  cancelled: 'gray',
};

/**
 * Step 1: pick a file. The server reads it (whatever the format), keeps the original and imports every ready row right
 * away with the settings chosen here; the import page then lists what still needs a person.
 */
export function ImportsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params] = useSearchParams();
  const { user } = useAuth();
  const { meta } = useLookups();
  const input = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  // Only what the user changed; the rest follows the defaults below, which appear once the lookups have loaded.
  const [changes, setChanges] = useState<Partial<ImportOptions>>({});
  const [matchColumns, setMatchColumns] = useState(false);
  const csvSourceId = meta?.sources.find((source) => source.key === 'csv_import' && source.isActive)?.id;
  const options: ImportOptions = {
    defaultOwnerId: user?.role === 'sales_rep' ? user.id : null,
    defaultSourceId: csvSourceId ?? null,
    defaultTagIds: [],
    defaultCountry: meta?.settings.defaultCountry ?? 'CA',
    duplicatePolicy: 'review',
    defaultConsentBasis: 'none',
    // Importing from the Dealers or Reps list (/imports?kind=dealer) presets what the file holds.
    businessKind: BUSINESS_KINDS.find((kind) => kind === params.get('kind')) ?? 'pizzeria',
    ...changes,
  };
  const ready = validCountry(options.defaultCountry);
  const { data } = useQuery({
    queryKey: ['imports', 'list'],
    queryFn: ({ signal }) => api.get<{ items: ImportJobDTO[] }>('/api/imports', { signal }),
  });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_IMPORT_BYTES) throw new Error(`This file is larger than ${MAX_IMPORT_BYTES / 1024 / 1024} MB. Split it into smaller files.`);
      const { defaultSourceId, ...rest } = options;
      // No "CSV import" source to show and none picked: leave it out so the server's default source applies.
      const settings: ImportUploadSettings = { ...rest, ...(defaultSourceId !== null || 'defaultSourceId' in changes ? { defaultSourceId } : {}), matchColumns };
      // The original bytes go up as they are: the server works out the format and the text encoding.
      const form = new FormData();
      form.append('settings', JSON.stringify(settings));
      form.append('file', file, file.name);
      return api.post<ImportJobDTO>('/api/imports', form);
    },
    onSuccess: (job) => navigate(`/imports/${job.id}`),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const pick = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (!ready) toast.error('Enter a two-letter country code first.');
    else upload.mutate(file);
  };

  return (
    <Page width="wide">
      <PageHeader
        title="Import leads"
        description="Bring in a trade-show list or an old spreadsheet. Leads are created right away with everything the file holds; anything unsure — possible duplicates, rows that don’t look like leads — is left for you to review."
      />
      <div className="mb-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Card className="min-w-0 self-start">
          <CardBody className="space-y-3">
            <div
              className={`flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center ${dragging ? 'border-ember-400 bg-ember-50' : 'border-slate-200'}`}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                pick(event.dataTransfer.files);
              }}
            >
              <FileUp className="size-8 text-slate-400" />
              <p className="text-[13px] text-slate-600">
                Drop a file with your leads here: Excel, CSV, Google Sheets exports, PDF, Word, contacts (vCard), JSON, XML, web pages or plain text (up to{' '}
                {MAX_IMPORT_BYTES / 1024 / 1024} MB and {MAX_IMPORT_ROWS.toLocaleString()} rows)
              </p>
              <input
                ref={input}
                type="file"
                accept={IMPORT_FILE_EXTENSIONS.join(',')}
                className="hidden"
                onChange={(event) => {
                  pick(event.target.files);
                  // Choosing the same file again (after fixing it) must upload again.
                  event.target.value = '';
                }}
              />
              <div className="flex flex-wrap justify-center gap-2">
                <Button variant="primary" icon={Upload} disabled={!ready} loading={upload.isPending} onClick={() => input.current?.click()}>
                  Choose a file
                </Button>
                <Button icon={FolderSearch} onClick={() => navigate('/imports/scan')}>
                  Scan a folder for lead files
                </Button>
              </div>
              <p className="text-xs text-slate-400">Google Sheets: File → Download → Microsoft Excel or CSV. Photos and scanned pages cannot be read.</p>
            </div>
            <Checkbox
              label="Let me check the column matching before anything is imported"
              checked={matchColumns}
              onChange={(event) => setMatchColumns(event.target.checked)}
            />
          </CardBody>
        </Card>
        <div className="space-y-4">
          <ImportOptionCards value={options} onChange={(patch) => setChanges((current) => ({ ...current, ...patch }))} />
        </div>
      </div>

      <Card>
        <CardHeader title="Previous imports" />
        {(data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={FileUp} title="No imports yet" />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data!.items.map((job) => (
              <li key={job.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-[13px]">
                <Link to={`/imports/${job.id}`} className="font-medium text-slate-900 hover:text-ember-700">
                  {job.filename}
                </Link>
                <Badge color={IMPORT_STATUS_COLORS[job.status]} size="sm">
                  {IMPORT_STATUS_LABELS[job.status]}
                </Badge>
                {job.batchId && (
                  <Link to={`/imports/batch/${job.batchId}`} className="text-xs text-slate-500 hover:text-ember-700">
                    Folder import
                  </Link>
                )}
                <span className="ml-auto text-slate-600">
                  {[
                    `${job.totalRows} rows`,
                    ...(job.status === 'validated' || job.status === 'completed'
                      ? [
                          `${job.createdCount} created`,
                          `${job.mergedCount} merged`,
                          job.unresolvedDuplicates > 0 && `${job.unresolvedDuplicates} to review`,
                          `${job.skippedCount + job.failedCount + job.invalidRows} not imported`,
                        ]
                      : []),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
                <span className="text-xs text-slate-400">
                  {job.createdBy?.name} · <RelativeTime value={job.createdAt} />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}
